/**
 * Web room page: draws one-call-per-account notices (accountDeviceNotices.ts)
 * as plain DOM over the conference — a full-screen dialog for anything that
 * needs a decision, a small banner for "Moving this call…". Plain DOM rather
 * than a React tree so the same overlay also serves room navigation that
 * failed before the conference UI ever mounted (app/actions.web.ts).
 *
 * All text is set through textContent: device labels and messages come from
 * the server and are never parsed as HTML.
 */
import { destroyLocalTracks } from '../base/tracks/actions.any';
import { LAPSED_NOTICE_MS, describeAccountDeviceNotice, isLiveNotice } from './accountDeviceNotices';
export const ACCOUNT_DEVICE_OVERLAY_ID = 'vrs-account-device-overlay';
export const ACCOUNT_DEVICE_BANNER_ID = 'vrs-account-device-banner';
const OVERLAY_STYLE = [
    'position:fixed', 'inset:0', 'z-index:2147483000', 'display:flex', 'align-items:center',
    'justify-content:center', 'padding:24px', 'box-sizing:border-box',
    'background:var(--color-surface-bg, #0b1220)', 'color:var(--color-text-primary, #f8fafc)',
    'font-family:var(--font-family, inherit)'
].join(';');
const PANEL_STYLE = 'max-width:520px;width:100%;text-align:center;display:flex;flex-direction:column;gap:12px';
const TITLE_STYLE = 'margin:0;font-size:1.5rem;line-height:1.3;font-weight:600';
const LINE_STYLE = 'margin:0;font-size:1rem;line-height:1.5;color:var(--color-text-secondary, #cbd5e1)';
const ACTIONS_STYLE = 'display:flex;flex-wrap:wrap;gap:12px;justify-content:center;margin-top:12px';
const BUTTON_STYLE = 'min-height:44px;padding:10px 20px;border-radius:var(--radius-sm, 8px);font-size:1rem;cursor:pointer;'
    + 'border:1px solid var(--color-border-default, #475569);background:transparent;color:inherit';
const PRIMARY_BUTTON_STYLE = 'min-height:44px;padding:10px 20px;border-radius:var(--radius-sm, 8px);font-size:1rem;'
    + 'cursor:pointer;border:0;background:var(--color-action-primary, #2563eb);color:var(--color-text-on-action, #fff)';
const BANNER_STYLE = [
    'position:fixed', 'top:16px', 'left:50%', 'transform:translateX(-50%)', 'z-index:2147482000',
    'max-width:min(560px, calc(100vw - 32px))', 'padding:12px 16px', 'border-radius:var(--radius-sm, 8px)',
    'background:var(--color-surface-raised, #1e293b)', 'color:var(--color-text-primary, #f8fafc)',
    'box-shadow:0 4px 16px rgba(0,0,0,.35)', 'font-size:1rem', 'line-height:1.4', 'text-align:center'
].join(';');
const BLOCKING_KINDS = ['superseded', 'moved', 'held', 'callOpenElsewhere'];
/**
 * A presenter bound to a document. One blocking notice is shown at a time
 * (the most recent); banners share one strip.
 */
export function createAccountDevicePresenter(doc, timers) {
    let blocking;
    let banner;
    let ticker;
    let lapsedTimer;
    const element = (tag, style, text) => {
        const node = doc.createElement(tag);
        node.style.cssText = style;
        if (text !== undefined) {
            node.textContent = text;
        }
        return node;
    };
    const syncTicker = () => {
        const live = Boolean(blocking && isLiveNotice(blocking.notice));
        if (live && ticker === undefined) {
            ticker = timers.setInterval(renderBlocking, 1000);
        }
        else if (!live && ticker !== undefined) {
            timers.clearInterval(ticker);
            ticker = undefined;
        }
    };
    let rendered;
    function renderBlocking() {
        if (!blocking) {
            doc.getElementById(ACCOUNT_DEVICE_OVERLAY_ID)?.remove();
            rendered = undefined;
            syncTicker();
            return;
        }
        const { notice } = blocking;
        const view = describeAccountDeviceNotice(notice, timers.now());
        const actionKey = view.actions.map(action => `${action.id}:${action.label}`).join('|');
        // Same dialog, new text (the per-second refresh of elapsed/"checked"
        // times, or a fresh status answer): update in place so the buttons —
        // and keyboard focus on them — survive.
        if (rendered && rendered.kind === notice.kind && rendered.actionKey === actionKey
            && doc.getElementById(ACCOUNT_DEVICE_OVERLAY_ID)) {
            rendered.title.textContent = view.title;
            rendered.body.textContent = '';
            view.body.forEach(line => rendered?.body.appendChild(element('p', LINE_STYLE, line)));
            syncTicker();
            return;
        }
        doc.getElementById(ACCOUNT_DEVICE_OVERLAY_ID)?.remove();
        const overlay = element('div', OVERLAY_STYLE);
        const panel = element('div', PANEL_STYLE);
        const title = element('h1', TITLE_STYLE, view.title);
        const body = element('div', PANEL_STYLE);
        const actions = element('div', ACTIONS_STYLE);
        let primary;
        overlay.id = ACCOUNT_DEVICE_OVERLAY_ID;
        overlay.className = `vrs-account-device-overlay vrs-account-device-${notice.kind}`;
        overlay.setAttribute('role', 'alertdialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-labelledby', `${ACCOUNT_DEVICE_OVERLAY_ID}-title`);
        title.id = `${ACCOUNT_DEVICE_OVERLAY_ID}-title`;
        body.setAttribute('aria-live', 'polite');
        panel.appendChild(title);
        view.body.forEach(line => body.appendChild(element('p', LINE_STYLE, line)));
        panel.appendChild(body);
        view.actions.forEach(action => {
            const button = element('button', action.primary ? PRIMARY_BUTTON_STYLE : BUTTON_STYLE, action.label);
            button.type = 'button';
            button.setAttribute('data-action', action.id);
            button.addEventListener('click', () => {
                // Look the handler up at click time: a re-presented notice of
                // the same kind may carry new handlers.
                const current = blocking?.notice.kind === notice.kind ? blocking.handlers[action.id] : undefined;
                if (current) {
                    current();
                }
                else if (action.id === 'close') {
                    dismiss(notice.kind);
                }
            });
            actions.appendChild(button);
            if (action.primary && !primary) {
                primary = button;
            }
        });
        panel.appendChild(actions);
        overlay.appendChild(panel);
        doc.body?.appendChild(overlay);
        rendered = { actionKey, body, kind: notice.kind, title };
        try {
            primary?.focus?.();
        }
        catch {
            // Focus is a courtesy; the dialog is already on screen.
        }
        syncTicker();
    }
    function renderBanner() {
        doc.getElementById(ACCOUNT_DEVICE_BANNER_ID)?.remove();
        if (!banner) {
            return;
        }
        const view = describeAccountDeviceNotice(banner.notice, timers.now());
        const strip = element('div', BANNER_STYLE, [view.title, ...view.body].join(' '));
        strip.id = ACCOUNT_DEVICE_BANNER_ID;
        strip.className = `vrs-account-device-banner vrs-account-device-${banner.notice.kind}`;
        strip.setAttribute('role', 'status');
        strip.setAttribute('aria-live', 'polite');
        doc.body?.appendChild(strip);
    }
    function clearLapsedTimer() {
        if (lapsedTimer !== undefined) {
            timers.clearTimeout(lapsedTimer);
            lapsedTimer = undefined;
        }
    }
    function present(notice, handlers) {
        if (BLOCKING_KINDS.includes(notice.kind)) {
            // A decision replaces any "moving" banner: that move is settled.
            if (notice.kind === 'moved') {
                clearLapsedTimer();
                banner = undefined;
                renderBanner();
            }
            blocking = { notice, handlers };
            renderBlocking();
            return;
        }
        clearLapsedTimer();
        banner = { notice, handlers };
        renderBanner();
        if (notice.kind === 'lapsed') {
            lapsedTimer = timers.setTimeout(() => {
                lapsedTimer = undefined;
                if (banner?.notice.kind === 'lapsed') {
                    banner = undefined;
                    renderBanner();
                }
            }, LAPSED_NOTICE_MS);
        }
    }
    function dismiss(kind) {
        if (blocking?.notice.kind === kind) {
            blocking = undefined;
            renderBlocking();
        }
        if (banner?.notice.kind === kind) {
            clearLapsedTimer();
            banner = undefined;
            renderBanner();
        }
    }
    return { dismiss, present };
}
let pagePresenter;
function getPagePresenter() {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
        return undefined;
    }
    if (!pagePresenter) {
        pagePresenter = createAccountDevicePresenter(document, {
            clearInterval: id => window.clearInterval(id),
            clearTimeout: id => window.clearTimeout(id),
            now: () => Date.now(),
            setInterval: (callback, ms) => window.setInterval(callback, ms),
            setTimeout: (callback, ms) => window.setTimeout(callback, ms)
        });
    }
    return pagePresenter;
}
/** Native binds the store's dispatch here; the web presenter draws DOM and needs none. */
export function bindAccountDeviceDispatch(_dispatch) {
    // Intentionally empty on web.
}
export function presentAccountDeviceNotice(notice, handlers) {
    getPagePresenter()?.present(notice, handlers);
}
export function dismissAccountDeviceNotice(kind) {
    getPagePresenter()?.dismiss(kind);
}
/**
 * Whether this page is a conference room (the notices belong there; the
 * static dashboards handle the same messages themselves).
 */
export function isAccountDeviceRoomPage() {
    const pathname = typeof window !== 'undefined' ? window.location?.pathname : undefined;
    const last = pathname?.split('/').filter(Boolean).pop();
    return Boolean(last && !last.endsWith('.html'));
}
/**
 * Leave the conference on this device WITHOUT ending the call (it moved to
 * another device of the account). Unlike hangup(), this does not redirect,
 * so the "This call moved" dialog stays on screen.
 */
export function leaveConferenceWithoutEnding(dispatch) {
    try {
        const conference = typeof APP !== 'undefined' ? APP.conference : undefined;
        if (conference && typeof conference.leaveRoom === 'function') {
            void Promise.resolve(conference.leaveRoom()).catch(() => undefined);
        }
    }
    catch {
        // The dialog still explains where the call went.
    }
    // The camera and microphone belong to the other device now.
    try {
        dispatch(destroyLocalTracks());
    }
    catch {
        // Track teardown is best-effort once the room is left.
    }
}
/**
 * This room, asking the server to move the account's call here. The token
 * helper sees moveCall=1, mints with moveCall:true, and drops the flag.
 */
export function roomURLWithMoveCall(href = window.location.href) {
    const url = new URL(href);
    url.searchParams.delete('jwt');
    url.searchParams.set('moveCall', '1');
    return url.toString();
}
/**
 * Page-level actions for the notices: "Use this device instead" after a
 * supersede reloads (a fresh load re-authenticates), and "Move it back here"
 * reopens this room asking for the call.
 */
export function accountDevicePageActions() {
    return {
        moveBack: () => window.location.assign(roomURLWithMoveCall()),
        reload: () => window.location.reload()
    };
}
